import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkle, PenNib, Check, CaretDown, ArrowRight,
  Lightning, ShieldCheck, Quotes, ShareNetwork, ImageSquare,
  MagnifyingGlass, TrendUp, ChartBar, UploadSimple, Clock, Lightbulb, Wallet, UsersThree,
  Brain, RocketLaunch, CheckSquareOffset, X as XIcon, EnvelopeSimple, Play,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { getBillingPlans } from "@/lib/api";
import InteractiveDemo, { WatchDemoButton } from "@/components/landing/InteractiveDemo";
import MarketingLayout, { Seo } from "@/components/marketing/MarketingLayout";
import MonthSimulation from "@/components/marketing/MonthSimulation";
import DemoRequestDialog from "@/components/landing/DemoRequestDialog";
import { AnimatedStats, BeforeAfter, ComparisonTable, RoiCalculator, TrustSecurity, TestimonialsSection, CtaBand, StickyCtaBar } from "@/components/landing/GrowthSections";

const PROBLEMS = [
  { icon: Clock, title: "No time for marketing", desc: "Your AI team creates weeks of content in minutes — marketing gets done even when you're busy running the business." },
  { icon: Lightbulb, title: "Don't know what to post", desc: "Vozelto plans your calendar and writes every post, caption and hashtag for you — always on-brand." },
  { icon: EnvelopeSimple, title: "Forget to send emails", desc: "Email campaigns are drafted, scheduled and sent on time, automatically. No more \u201cwe should really email our customers.\u201d" },
  { icon: TrendUp, title: "Want more leads", desc: "AI-optimized posts, SEO and ads built to bring more customers to your door — not just likes." },
  { icon: Wallet, title: "Can't afford a marketing agency", desc: "Get an entire marketing department for a fraction of one agency retainer — no contracts, cancel anytime." },
];

const AI_TEAM = [
  { icon: ShareNetwork, name: "AI Social Media Manager", desc: "Plans, writes and publishes your Facebook & Instagram posts so your pages stay active — without you lifting a finger." },
  { icon: PenNib, name: "AI Copywriter", desc: "Writes blogs, ads and website copy in your brand's voice — professional words in seconds, not hours." },
  { icon: EnvelopeSimple, name: "AI Email Marketing Manager", desc: "Creates and schedules email campaigns that keep customers coming back — automatically." },
  { icon: ImageSquare, name: "AI Image Creator", desc: "Turns your products and logo into scroll-stopping marketing images — no designer needed." },
  { icon: ChartBar, name: "AI Marketing Analyst", desc: "Watches your numbers and tells you what's working — and what to do next — in plain English." },
  { icon: MagnifyingGlass, name: "AI SEO Assistant", desc: "Improves how you show up on Google so nearby customers find you before your competitors." },
];

const BENEFITS = [
  "Create a month of social media in minutes",
  "Write professional emails instantly",
  "Generate marketing images with AI",
  "Improve your SEO and get found on Google",
  "Track marketing performance at a glance",
  "Keep every campaign organized in one place",
];

const INDUSTRIES = [
  "Landscapers", "Contractors", "Roofers", "Electricians", "Plumbers",
  "HVAC Companies", "Realtors", "Retail Stores", "Coffee Shops", "Small Businesses",
];

const STEPS = [
  { icon: UploadSimple, title: "Upload your brand", desc: "Add your logo, colors, voice and products to the Brand Assets library — once." },
  { icon: Brain, title: "AI learns your business", desc: "Every AI employee studies your brand, catalog and goals before writing a word." },
  { icon: RocketLaunch, title: "AI creates complete campaigns", desc: "Content, images, ads, emails and social posts — generated as one coordinated push." },
  { icon: CheckSquareOffset, title: "Review, approve & publish", desc: "Everything lands in your Approval Queue with a readiness score. You stay in control." },
];

const SHOWCASE = [
  { key: "approvals", name: "Approval Queue", img: "/shots/vozelto-approval-queue.jpg",
    benefit: "Nothing goes live without you. Every post, email and ad waits for your sign-off — with comments, edits and one-click AI improvements." },
  { key: "readiness", name: "Campaign Readiness Score", img: "/shots/vozelto-approval-queue.jpg",
    benefit: "A 100-point agency-style review on every design — brand consistency, readability, CTA strength — so you publish with confidence, not guesswork." },
  { key: "image-studio", name: "AI Image Studio", img: "/shots/vozelto-image-studio.jpg",
    benefit: "Product photography and ad creative that looks like a $5k shoot — built with your real logo and products, scored by AI vision before you see it." },
  { key: "social", name: "AI Social Manager", img: "/shots/vozelto-social-manager.jpg",
    benefit: "Wake up to a week of on-brand posts, captions and hashtags — then publish straight to Facebook and Instagram from one place." },
  { key: "calendar", name: "Marketing Calendar", img: "/shots/vozelto-marketing-calendar.jpg",
    benefit: "Your whole month planned on one calendar. No more 'what are we posting today?' — your marketing runs on schedule." },
  { key: "dashboard", name: "Executive Dashboard", img: "/shots/vozelto-executive-dashboard.jpg",
    benefit: "One screen tells you how the business is doing and what your AI team recommends next. Built for owners, not analysts." },
  { key: "analytics", name: "Analytics", img: "/shots/vozelto-analytics.jpg",
    benefit: "Traffic, revenue, ads and conversion in plain English — with the 'so what' included, not just charts." },
  { key: "brand-assets", name: "Brand Assets", img: "/shots/vozelto-brand-assets.jpg",
    benefit: "One brand library powers every AI employee. Your logo, voice and colors show up correctly, everywhere, automatically." },
  { key: "templates", name: "Templates", img: "/shots/vozelto-templates.jpg",
    benefit: "20+ proven campaign templates for every channel and industry — personalized to your brand and launched in one click." },
  { key: "employees", name: "AI Employees", img: "/shots/vozelto-ai-employees.jpg",
    benefit: "See your whole AI team, what they've produced and what they're working on — like a staff page for your marketing department." },
];

const REPLACED_TOOLS = [
  "Social media scheduler", "Email marketing tool", "SEO software", "Blog writing AI",
  "Image generator", "Analytics platform", "Advertising software",
];

const FAQS = [
  { q: "What is Vozelto?", a: "Vozelto is your complete AI marketing team in one platform. Twelve AI specialists — from a Marketing Director to an Image Studio — create content, social posts, ads, emails, SEO and growth strategy together, using your real brand assets." },
  { q: "How is it different from ChatGPT?", a: "ChatGPT is a blank chat box. Vozelto is a coordinated team that knows your brand, products and data — it creates finished campaigns with your real logo and voice, routes everything through an approval queue, and publishes directly to your channels." },
  { q: "Does it publish directly to Facebook and Instagram?", a: "Yes. Connect your Meta account once and approved posts publish straight to your Facebook Page and Instagram — no copy-pasting." },
  { q: "Can I approve everything before it goes live?", a: "Always. Every piece of content lands in your Approval Queue with a Campaign Readiness Score. Nothing publishes without your explicit approval." },
  { q: "Does it work with Shopify?", a: "Yes. Vozelto connects to your Shopify store to read products, orders and customers — powering product intelligence, revenue recommendations and on-brand product creative." },
  { q: "Can agencies manage multiple clients?", a: "Yes. Multi-workspace support lets agencies run every client from one command center, with isolated data, brand assets and billing per workspace." },
  { q: "Can I cancel anytime?", a: "Yes. Cancel anytime from your billing page — your plan stays active until the end of the current period and you won't be charged again. Every plan starts with a 14-day free trial." },
];

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(24px)";
    el.style.transition = "opacity .6s ease, transform .6s ease";
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
        io.disconnect();
      }
    }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
};

const Reveal = ({ children, className = "" }) => {
  const ref = useReveal();
  return <div ref={ref} className={className}>{children}</div>;
};

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="card-matte overflow-hidden">
      <button data-testid={`faq-item-${index}`} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
        <span className="font-head font-semibold text-white">{item.q}</span>
        <CaretDown size={18} className={`shrink-0 brand-text transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`px-5 text-[#9a9a9a] text-sm leading-relaxed transition-all duration-300 ${open ? "max-h-60 pb-5 opacity-100" : "max-h-0 opacity-0"}`}>
        {item.a}
      </div>
    </div>
  );
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [interval, setInterval] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoAuto, setDemoAuto] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [showcase, setShowcase] = useState(SHOWCASE[0]);

  useEffect(() => {
    getBillingPlans().then((r) => setPlans(r.plans || [])).catch(() => {});
  }, []);

  const go = () => navigate("/signup");

  const priced = useMemo(() => plans.map((p) => ({
    ...p,
    price: interval === "monthly" ? p.monthly : p.yearly,
    sub: interval === "monthly" ? "/mo" : "/yr",
    highlight: p.id === "growth",
  })), [plans, interval]);

  return (
    <MarketingLayout>
      <Seo title="Vozelto — Your AI Marketing Team. One Platform." path="/"
        description="Stop spending hours on social media, emails, ads and SEO. Vozelto's AI marketing team creates, manages and optimizes your marketing from one simple platform. Start your free 14-day trial." />
      <div data-testid="landing-page">
      <InteractiveDemo open={demoOpen} autoPlay={demoAuto} onClose={() => setDemoOpen(false)} />
      <DemoRequestDialog open={bookOpen} onClose={() => setBookOpen(false)} source="landing_page" />
      <StickyCtaBar onBookDemo={() => setBookOpen(true)} />

      {/* 1 · HERO */}
      <section className="relative">
        <div className="absolute -top-24 -left-24 h-[28rem] w-[28rem] rounded-full bg-[#6B3DFF]/15 blur-[120px] pointer-events-none" />
        <div className="absolute top-40 right-0 h-80 w-80 rounded-full bg-[#6B3DFF]/10 blur-[120px] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-5 pt-20 pb-16 md:pt-28 md:pb-24">
          <div className="max-w-3xl fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#6B3DFF]/30 bg-[#6B3DFF]/10 px-3 py-1 text-xs font-semibold brand-text mb-6">
              <Lightning size={13} weight="fill" /> THE AI OPERATING SYSTEM FOR MODERN BUSINESS
            </span>
            <h1 className="font-head font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Marketing That<br /><span className="brand-text">Runs Itself.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#b5b5b5] max-w-2xl leading-relaxed">
              Your AI marketing team plans, writes, designs and publishes your social media, emails, ads and SEO —
              you just approve. Vozelto gives your business an entire marketing department from one simple
              platform, so you can get back to running the business.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button data-testid="hero-start-trial" onClick={go} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full px-7 h-12 text-base">
                Start Free 14-Day Trial <ArrowRight size={18} className="ml-1.5" weight="bold" />
              </Button>
              <WatchDemoButton onOpen={() => setDemoOpen(true)} testid="hero-watch-demo" label="Watch 2-Minute Demo" />
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[#7a7a7a]" data-testid="hero-trust-badges">
              <span className="flex items-center gap-1.5"><Check size={14} className="brand-text" weight="bold" /> 14-Day Free Trial</span>
              <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="brand-text" /> Secure Stripe Billing</span>
              <span className="flex items-center gap-1.5"><Check size={14} className="brand-text" weight="bold" /> Cancel Anytime</span>
            </div>
          </div>

          {/* real product shot */}
          <Reveal className="mt-14">
            <div className="card-matte brand-glow p-2 max-w-4xl">
              <img src="/shots/vozelto-executive-dashboard.jpg" alt="Vozelto Command Center — executive dashboard"
                width="1600" height="900" fetchPriority="high"
                className="rounded-lg border border-white/5 w-full" data-testid="hero-product-shot" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 1b · ANIMATED STATS */}
      <AnimatedStats />

      {/* 1c · SIGNATURE — 60-second month */}
      <MonthSimulation />

      {/* 1c · WHY VOZELTO — problems we solve */}
      <section className="border-y border-white/5 bg-[#0a0a0a]/60">
        <div className="max-w-6xl mx-auto px-5 py-16 md:py-24">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <div className="label-caps mb-3">Why Vozelto</div>
            <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight">Stop Doing Marketing the Hard Way</h2>
            <p className="mt-3 text-[#9a9a9a]">Sound familiar? Every one of these problems is exactly what your AI team was built to fix.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="problems-grid">
            {PROBLEMS.map((p, i) => (
              <Reveal key={p.title}>
                <div data-testid={`problem-card-${i}`} className="card-matte p-6 h-full transition-all duration-300 hover:-translate-y-1 hover:border-[#6B3DFF]/30 border border-transparent">
                  <div className="h-10 w-10 rounded-xl bg-[#6B3DFF]/12 flex items-center justify-center mb-3">
                    <p.icon size={20} className="brand-text" weight="duotone" />
                  </div>
                  <h3 className="font-head font-semibold">&ldquo;{p.title}&rdquo;</h3>
                  <p className="mt-2 text-sm text-[#9a9a9a] leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 1d · BEFORE vs AFTER */}
      <BeforeAfter />
      <CtaBand onBookDemo={() => setBookOpen(true)} headline="Ready to switch marketing to easy mode?" />

      {/* 2 · MEET YOUR AI TEAM */}
      <section id="team" className="max-w-6xl mx-auto px-5 py-16 md:py-24">
        <Reveal className="max-w-2xl">
          <div className="label-caps mb-3">Your new employees</div>
          <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight">Meet Your AI Team</h2>
          <p className="mt-3 text-[#9a9a9a]">Six AI specialists that work like full-time employees — they know your brand, your products and your goals, and they never miss a deadline.</p>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ai-team-grid">
          {AI_TEAM.map((e, i) => (
            <Reveal key={e.name}>
              <div data-testid={`employee-${i}`}
                className="card-matte p-6 h-full group transition-all duration-300 hover:-translate-y-1 hover:border-[#6B3DFF]/30 border border-transparent cursor-default">
                <div className="h-11 w-11 rounded-xl bg-[#6B3DFF]/12 flex items-center justify-center mb-3 transition-all duration-300 group-hover:bg-[#6B3DFF]/25 group-hover:scale-110">
                  <e.icon size={22} className="brand-text" weight="duotone" />
                </div>
                <h3 className="font-head font-semibold leading-tight">{e.name}</h3>
                <p className="mt-2 text-sm text-[#9a9a9a] leading-relaxed">{e.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-6">
          <div className="card-matte px-5 py-4 flex items-center gap-3 text-sm text-[#b5b5b5]">
            <Sparkle size={18} className="brand-text shrink-0" weight="fill" />
            Plus six more specialists on every plan — Marketing Director, Sales Manager, Advertising Manager, Website Optimizer, Revenue Manager and Brand Manager — all sharing one brand memory and one Approval Queue you control.
          </div>
        </Reveal>
      </section>

      {/* 3 · HOW VOZELTO WORKS */}
      <section className="border-y border-white/5 bg-[#0a0a0a]/60">
        <div className="max-w-6xl mx-auto px-5 py-16 md:py-24">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <div className="label-caps mb-3">How Vozelto works</div>
            <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight">From brand upload to published campaign</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="how-it-works">
            {STEPS.map((s, i) => (
              <Reveal key={s.title}>
                <div data-testid={`workflow-step-${i}`} className="relative card-matte p-6 h-full">
                  <div className="absolute -top-3 left-5 h-7 w-7 rounded-full bg-[#6B3DFF] text-white font-head font-bold text-sm flex items-center justify-center">{i + 1}</div>
                  <s.icon size={26} className="brand-text mb-3 mt-2" weight="duotone" />
                  <h3 className="font-head font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm text-[#9a9a9a] leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3b · BENEFITS */}
      <section className="max-w-6xl mx-auto px-5 py-16 md:py-24" data-testid="benefits-section">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div className="label-caps mb-3">What you get</div>
            <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight">Save Time. <span className="brand-text">Grow Faster.</span></h2>
            <p className="mt-3 text-[#9a9a9a] leading-relaxed">No new skills to learn, no tools to juggle. Just tell Vozelto about your business — your AI team handles the rest.</p>
            <Button data-testid="benefits-start-trial" onClick={go} className="mt-6 bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full px-6 h-11">
              Start Free 14-Day Trial <ArrowRight size={16} className="ml-1.5" weight="bold" />
            </Button>
          </Reveal>
          <Reveal>
            <div className="grid sm:grid-cols-2 gap-3">
              {BENEFITS.map((b, i) => (
                <div key={b} data-testid={`benefit-${i}`} className="card-matte px-4 py-3.5 flex items-start gap-3">
                  <Check size={17} className="brand-text mt-0.5 shrink-0" weight="bold" />
                  <span className="text-sm text-[#e6e6e6] leading-snug">{b}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4 · FEATURE SHOWCASE */}
      <section id="features" className="max-w-6xl mx-auto px-5 py-16 md:py-24">
        <Reveal className="max-w-2xl">
          <div className="label-caps mb-3">Inside the platform</div>
          <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight">Real product. Real screenshots.</h2>
          <p className="mt-3 text-[#9a9a9a]">This isn't a mockup — it's Vozelto working on a live brand.</p>
        </Reveal>
        <div className="mt-10 grid lg:grid-cols-5 gap-5">
          <div className="lg:col-span-2 space-y-1.5" data-testid="showcase-tabs">
            {SHOWCASE.map((f) => (
              <button key={f.key} data-testid={`showcase-tab-${f.key}`} onClick={() => setShowcase(f)}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-all duration-200 ${showcase.key === f.key ? "border-[#6B3DFF]/35 bg-[#6B3DFF]/[0.07]" : "border-white/8 hover:border-white/20"}`}>
                <div className={`text-sm font-semibold ${showcase.key === f.key ? "text-white" : "text-[#9a9a9a]"}`}>{f.name}</div>
                {showcase.key === f.key && <div className="text-xs text-[#b5b5b5] mt-1 leading-relaxed">{f.benefit}</div>}
              </button>
            ))}
          </div>
          <div className="lg:col-span-3">
            <div className="card-matte brand-glow p-2 sticky top-24">
              <img key={showcase.key} src={showcase.img} alt={showcase.name} width="1600" height="900" loading="lazy"
                className="rounded-lg border border-white/5 w-full demo-fade" data-testid="showcase-image" />
            </div>
          </div>
        </div>
      </section>

      {/* 5 · WHY VOZELTO */}
      <section className="border-y border-white/5 bg-[#0a0a0a]/60">
        <div className="max-w-6xl mx-auto px-5 py-16 md:py-24">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <div className="label-caps mb-3">Replace your tool stack</div>
            <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight">Stop paying for seven tools that don't talk to each other</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6 items-stretch" data-testid="comparison-section">
            <Reveal>
              <div className="card-matte p-7 h-full">
                <div className="label-caps mb-4 text-[#7a7a7a]">The old way</div>
                <ul className="space-y-3">
                  {REPLACED_TOOLS.map((t) => (
                    <li key={t} className="flex items-center gap-3 text-sm text-[#9a9a9a]">
                      <XIcon size={15} className="text-red-400/70 shrink-0" weight="bold" />
                      <span className="line-through decoration-white/20">{t}</span>
                      <span className="ml-auto text-xs text-[#5a5a5a]">$30–200/mo</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-white/8 text-sm text-[#9a9a9a]">7 subscriptions · 7 logins · zero coordination</div>
              </div>
            </Reveal>
            <Reveal>
              <div className="card-matte brand-glow p-7 h-full relative overflow-hidden">
                <div className="absolute -top-16 right-0 h-40 w-40 rounded-full bg-[#6B3DFF]/15 blur-[80px] pointer-events-none" />
                <div className="label-caps mb-4 brand-text">The Vozelto way</div>
                <ul className="space-y-3">
                  {["All 12 AI employees included", "One brand library powering everything", "One Approval Queue — you stay in control",
                    "Direct publishing to Facebook & Instagram", "Shopify, GA4 and Klaviyo connected", "One login. One bill. One team."].map((t) => (
                    <li key={t} className="flex items-center gap-3 text-sm text-[#e6e6e6]">
                      <Check size={15} className="brand-text shrink-0" weight="bold" /> {t}
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-white/8 text-sm font-semibold text-white">One Vozelto subscription replaces them all.</div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 5a2 · FEATURE COMPARISON TABLE */}
      <ComparisonTable />

      {/* 5b · DEMO VIDEO */}
      <section className="max-w-6xl mx-auto px-5 py-16 md:py-24" data-testid="demo-video-section">
        <Reveal className="text-center max-w-2xl mx-auto mb-10">
          <div className="label-caps mb-3">See it in action</div>
          <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight">Watch Vozelto run a real brand</h2>
          <p className="mt-3 text-[#9a9a9a]">A 2-minute guided walkthrough — auto-playing, with the actual product screens. No account needed.</p>
        </Reveal>
        <Reveal>
          <button data-testid="demo-video-play" onClick={() => { setDemoAuto(true); setDemoOpen(true); }}
            className="relative block w-full max-w-4xl mx-auto card-matte brand-glow p-2 rounded-2xl group cursor-pointer" aria-label="Play the Vozelto demo">
            <img src="/shots/vozelto-ai-employees.jpg" alt="Vozelto demo walkthrough preview" width="1600" height="900" loading="lazy"
              className="rounded-lg border border-white/5 w-full opacity-80 group-hover:opacity-60 transition-opacity" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="h-20 w-20 rounded-full bg-[#6B3DFF] flex items-center justify-center shadow-[0_0_60px_rgba(255,94,0,0.5)] transition-transform duration-300 group-hover:scale-110">
                <Play size={30} className="text-white ml-1" weight="fill" />
              </span>
            </span>
            <span className="absolute bottom-5 left-1/2 -translate-x-1/2 text-xs font-semibold text-white bg-black/60 backdrop-blur rounded-full px-4 py-1.5">▶ 2-minute guided demo</span>
          </button>
        </Reveal>
      </section>

      {/* 5c · WHO IT'S FOR */}
      <section className="border-y border-white/5 bg-[#0a0a0a]/60" data-testid="who-its-for-section">
        <div className="max-w-6xl mx-auto px-5 py-16 md:py-24 text-center">
          <Reveal className="max-w-2xl mx-auto">
            <div className="label-caps mb-3 flex items-center justify-center gap-2"><UsersThree size={16} className="brand-text" weight="duotone" /> Who it's for</div>
            <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight">Built for Busy Business Owners</h2>
            <p className="mt-3 text-[#9a9a9a] leading-relaxed">If you're too busy serving customers to think about marketing, Vozelto was built for you. It works for virtually any small or growing business that wants to save time and market like the big brands.</p>
          </Reveal>
          <Reveal className="mt-8 flex flex-wrap items-center justify-center gap-2.5 max-w-3xl mx-auto">
            {INDUSTRIES.map((ind) => (
              <span key={ind} data-testid={`industry-${ind.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-full border border-white/10 bg-[#14141F] px-4 py-2 text-sm text-[#cfcfcf] hover:border-[#6B3DFF]/40 hover:text-white transition-colors">
                {ind}
              </span>
            ))}
          </Reveal>
        </div>
      </section>

      {/* 5d · ROI CALCULATOR */}
      <RoiCalculator />

      {/* 6 · PRICING */}
      <section id="pricing" className="max-w-6xl mx-auto px-5 py-16 md:py-24">
        <Reveal className="text-center max-w-2xl mx-auto">
          <div className="label-caps mb-3">Pricing</div>
          <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight">Simple plans that scale with you</h2>
          <p className="mt-3 text-[#9a9a9a]">14-day free trial · Cancel anytime · No hidden fees</p>
          <div className="mt-6 inline-flex items-center rounded-full border border-white/10 bg-[#14141F] p-1" data-testid="pricing-toggle">
            {["monthly", "yearly"].map((iv) => (
              <button key={iv} data-testid={`pricing-${iv}`} onClick={() => setInterval(iv)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${interval === iv ? "bg-[#6B3DFF] text-white" : "text-[#9a9a9a] hover:text-white"}`}>
                {iv === "monthly" ? "Monthly" : "Yearly"}
                {iv === "yearly" && <span className="ml-1.5 text-[10px] opacity-80">save ~2mo</span>}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5 items-start">
          {priced.map((p) => (
            <div key={p.id} data-testid={`pricing-card-${p.id}`}
              className={`card-matte p-7 relative ${p.highlight ? "brand-glow border border-[#6B3DFF]/30" : ""}`}>
              {p.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#6B3DFF] text-white text-[10px] font-bold px-3 py-1 tracking-wide whitespace-nowrap">RECOMMENDED</span>
              )}
              <h3 className="font-head font-semibold text-xl">{p.name}</h3>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-head font-extrabold text-4xl">${p.price}</span>
                <span className="text-[#7a7a7a] mb-1.5 text-sm">{p.sub}</span>
              </div>
              <Button data-testid={`pricing-cta-${p.id}`} onClick={() => navigate(`/signup?plan=${p.id}&interval=${interval}`)}
                className={`mt-5 w-full rounded-full h-11 ${p.highlight ? "bg-[#6B3DFF] hover:bg-[#A56CFF] text-white" : "bg-white/10 hover:bg-white/15 text-white"}`}>
                Start Free Trial
              </Button>
              <ul className="mt-6 space-y-2.5">
                {(p.features || []).map((ft) => (
                  <li key={ft} className="flex items-start gap-2.5 text-sm text-[#cfcfcf]">
                    <Check size={16} className="brand-text mt-0.5 shrink-0" weight="bold" /> {ft}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {priced.length > 0 && (
            <div data-testid="pricing-card-enterprise" className="card-matte p-7 relative">
              <h3 className="font-head font-semibold text-xl">Enterprise</h3>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-head font-extrabold text-3xl">Custom</span>
              </div>
              <Button data-testid="pricing-cta-enterprise" onClick={() => window.open("mailto:info@vozelto.com?subject=Vozelto%20Enterprise", "_blank")}
                className="mt-5 w-full rounded-full h-11 bg-white/10 hover:bg-white/15 text-white">
                Contact Sales
              </Button>
              <ul className="mt-6 space-y-2.5">
                {["Unlimited workspaces", "Dedicated onboarding", "Custom integrations", "Priority support & SLA"].map((ft) => (
                  <li key={ft} className="flex items-start gap-2.5 text-sm text-[#cfcfcf]">
                    <Check size={16} className="brand-text mt-0.5 shrink-0" weight="bold" /> {ft}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {priced.length === 0 && <div className="text-[#7a7a7a] text-sm col-span-4 text-center">Loading plans…</div>}
        </div>
      </section>

      {/* 6b · TRUST & SECURITY */}
      <TrustSecurity />

      {/* 7 · TESTIMONIALS (API-driven, editable from Leads & Growth) */}
      <TestimonialsSection />
      <CtaBand onBookDemo={() => setBookOpen(true)} headline="Join them — your AI team starts today" />

      {/* 8 · FAQ */}
      <section className="max-w-3xl mx-auto px-5 py-16 md:py-24">
        <Reveal className="text-center mb-10">
          <div className="label-caps mb-3">FAQ</div>
          <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight">Questions, answered</h2>
        </Reveal>
        <div className="space-y-3">
          {FAQS.map((item, i) => <FaqItem key={i} item={item} index={i} />)}
        </div>
      </section>

      {/* 9 · FINAL CTA */}
      <section className="max-w-6xl mx-auto px-5 pb-20">
        <Reveal>
          <div className="card-matte brand-glow p-10 md:p-16 text-center relative overflow-hidden">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 h-60 w-60 rounded-full bg-[#6B3DFF]/20 blur-[100px] pointer-events-none" />
            <h2 className="relative font-head font-extrabold text-3xl md:text-5xl tracking-tight">Ready to Let AI Handle<br className="sm:hidden" /> Your Marketing?</h2>
            <p className="relative mt-4 text-[#b5b5b5] max-w-xl mx-auto">Join businesses using AI to save time, stay consistent, and grow faster. 14-day free trial, cancel anytime.</p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button data-testid="cta-start-trial" onClick={go} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full px-8 h-12 text-base">
                Start Free 14-Day Trial <ArrowRight size={18} className="ml-1.5" weight="bold" />
              </Button>
              <Button data-testid="cta-book-demo" variant="ghost" onClick={() => setBookOpen(true)}
                className="text-white hover:bg-white/5 rounded-full px-6 h-12 text-base border border-white/10">
                <EnvelopeSimple size={16} className="mr-2 brand-text" /> Book a Demo
              </Button>
            </div>
          </div>
        </Reveal>
      </section>

      </div>
    </MarketingLayout>
  );
}
